/**
 * LinkUsersForm — waarom een beheerder niet namens iemand koppelt.
 *
 * WAT HIER STOND, EN WAAROM HET WEG IS
 *
 * Dit was een formulier: kies een ouder of docent, kies een leerling, klik
 * Koppelen. Twee dingen waren daar fout aan, en de tweede is de zwaarste.
 *
 *   1. HET DEED NIETS OP DE SERVER. `linkParentToLearnerById` en
 *      `linkTeacherToLearnerById` zijn pure mutaties op de lokale store. Voor een
 *      account dat alleen op de server bestaat — en dat is in het adminportaal de
 *      norm — vonden ze niets om te wijzigen. Het formulier sloot, er kwam geen
 *      foutmelding, en er was geen koppeling. Dezelfde stille faalstand als het
 *      bewerkformulier vóór 0025.
 *
 *   2. HET OMZEILDE DE HELE KOPPELCODE-ARCHITECTUUR. Zou het wél naar de server
 *      schrijven, dan was het erger: dan is de zorgvuldigheid rond die code
 *      (hashen, eenmalig, ratelimiet, de limiet van vijf ouders) alleen van
 *      toepassing op wie geen support belt. Een beheerder die rechtstreeks kan
 *      koppelen, is de achterdeur van elk slot dat we hebben gebouwd.
 *
 * WAAROM EEN UITLEG EN GEEN UITGESCHAKELD FORMULIER
 *
 * Een grijs formulier met "binnenkort" leest als een belofte. Dit komt er niet,
 * en de vervanger bestaat al: een verse koppelcode die de OUDER zelf verzilvert,
 * en de klascode die de LEERKRACHT zelf gebruikt. In beide gevallen doet de
 * partij die het recht heeft de handeling, en support geeft alleen het middel.
 *
 * Zie `docs/specs/SHORT_CODE_SECURITY.md` §5d en migratie 0042.
 */

import React from 'react';
import { KeyRound, ShieldCheck, Users } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
interface LinkUsersFormProps {
  onClose: () => void;
}
export function LinkUsersForm({
  onClose
}: LinkUsersFormProps) {
  return <div className="space-y-4" data-id="element-7805" data-mp="qu7ztktjqxt5">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-id="element-7806" data-mp="qswx7obu7w3m">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900" data-id="element-7807" data-mp="uuihhuqpbcnu">
          <ShieldCheck className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-7808" data-mp="2rvo0b8z4nkkc" />
          Koppelen doet de ouder of leerkracht zelf
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-id="element-7809" data-mp="2mwbhczbdmxn1">
          Een beheerder koppelt niet namens iemand anders. Dat is geen ontbrekende
          functie: kon support dit, dan zou de beveiliging rond koppelcodes
          alleen gelden voor wie geen support belt. De partij die het recht heeft,
          doet de handeling — support geeft alleen het middel.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 p-4" data-id="element-7810" data-mp="247wl9xm7ctfd">
        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900" data-id="element-7811" data-mp="2hy02ga685wxy">
          <KeyRound className="h-4 w-4 shrink-0 text-indigo-500" aria-hidden="true" data-id="element-7812" data-mp="2d79waupz5q27" />
          Een ouder krijgt geen toegang
        </h4>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-id="element-7813" data-mp="1pg4iel3lh7uj">
          Open de leerling in de lijst en kies onder <strong data-id="element-7814" data-mp="1ki2qdao6xy6x">Supportacties</strong>
          {' '}
          voor <em data-id="element-7815" data-mp="1x75nqqfax">Nieuwe koppelcode uitgeven</em>. Geef die code door; de ouder
          verzilvert hem zelf in zijn portaal. Daarmee blijft de limiet van vijf
          ouders gelden, en blijft zichtbaar wie de koppeling heeft gemaakt.
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-slate-500" data-id="element-7816" data-mp="3n6uhmi7ypr58">
          Let op: een verse code maakt de vorige ongeldig. Had iemand anders die
          nog, dan werkt hij niet meer.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 p-4" data-id="element-7817" data-mp="jmggx0v78ln8">
        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900" data-id="element-7818" data-mp="npcrc4al5bg3">
          <Users className="h-4 w-4 shrink-0 text-indigo-500" aria-hidden="true" data-id="element-7819" data-mp="9p20v2d6tyvg" />
          Een leerkracht mist een leerling
        </h4>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-id="element-7820" data-mp="eraw65dj9d0q">
          De leerkracht voegt de leerling zelf toe met de klascode van zijn klas.
          Een beheerder kan niet vaststellen dat dit kind in die klas hoort; de
          leerkracht en de school kunnen dat wel. Blijft de klas leeg, controleer
          dan eerst of het account van de leerkracht geverifieerd is — een
          wachtend account krijgt geen enkele leerling te zien.
        </p>
      </div>

      <div className="flex justify-end pt-2" data-id="element-7821" data-mp="35xa48achkzfm">
        <Button type="button" variant="secondary" onClick={onClose} data-id="element-7822" data-mp="30xnonu96r73n">
          Sluiten
        </Button>
      </div>
    </div>;
}